import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NewsArticle } from '@/types/news';
import { getCategoryName } from '@/mocks/news';
import { timeAgo } from '@/utils/format';

export default function HeroCarousel({ articles }: { articles: NewsArticle[] }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const count = articles.length;

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const current = articles[index];

  const go = (i: number) => setIndex((i + count) % count);

  return (
    <section className="relative h-[380px] w-full overflow-hidden rounded-lg md:h-[480px]">
      <img
        key={current.id}
        src={current.cover}
        alt={current.title}
        title={`${current.title} - 911爆料网`}
        className="absolute inset-0 h-full w-full animate-fade-in object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/20"></div>

      <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-md bg-accent-500 px-2.5 py-1 font-semibold text-primary-950">
            焦点
          </span>
          <span className="rounded-md bg-background-50/15 px-2.5 py-1 font-medium text-background-50 backdrop-blur">
            {getCategoryName(current.category)}
          </span>
          <span className="text-background-200">{timeAgo(current.publishedAt)}</span>
        </div>
        <h2 className="mt-3 max-w-3xl font-heading text-xl font-extrabold leading-snug text-background-50 md:text-3xl">
          {current.title}
        </h2>
        <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-background-200 md:block">
          {current.summary}
        </p>
        <button
          type="button"
          onClick={() => navigate(`/detail/${current.id}`)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-semibold text-primary-800 transition-colors hover:bg-accent-400 hover:text-primary-950"
        >
          <i className="ri-article-line"></i>
          查看详情
        </button>
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="上一条"
            className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 backdrop-blur transition-colors hover:bg-primary-950/80 md:left-5 md:h-11 md:w-11"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="下一条"
            className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 backdrop-blur transition-colors hover:bg-primary-950/80 md:right-5 md:h-11 md:w-11"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
          <div className="absolute bottom-5 right-5 flex items-center gap-2 md:bottom-8 md:right-8">
            {articles.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到第 ${i + 1} 条`}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-6 bg-accent-500' : 'w-2 bg-background-50/60'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}